const DEFAULT_PORT = "443";
const MAX_PORT = 65535;
const MAX_LENGTH = 255;
/** Same shape as the backend (`services/webapp/frame_policy.py`): dotted lower-case labels, the last one starting with a letter. */
const ORIGIN = /^https:\/\/(?<host>(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?)(?::(?<port>\d{1,5}))?$/;
const SCHEME = /^[a-z][a-z\d+.-]*:\/\//;

export type FrameOriginProblem = "invalid" | "http" | "wildcard" | "path" | "address" | "credentials";
export type FrameOriginResult = { ok: true; value: string } | { ok: false; problem: FrameOriginProblem };

function problemOf(value: string): FrameOriginProblem {
  if (value.includes("*")) return "wildcard";
  if (value.includes("@")) return "credentials";
  if (value.startsWith("http://")) return "http";
  const rest = value.replace(SCHEME, "");
  if (/[/?#]/.test(rest)) return "path";
  if (/^\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?$/.test(rest) || rest.startsWith("[")) return "address";
  return "invalid";
}

/**
 * One typed entry -> the exact origin the API stores (`https://host[:port]`), or what is wrong with it.
 * Lenient where it is safe: a bare `www.example.com` gets `https://`, a lone trailing slash goes, `:443` goes,
 * and case is folded. Anything else the API would refuse is refused here with the reason.
 */
export function parseFrameOrigin(raw: string): FrameOriginResult {
  let value = raw.trim().toLowerCase();
  if (value.length > MAX_LENGTH) return { ok: false, problem: "invalid" };
  if (value !== "" && !SCHEME.test(value) && !value.includes("*") && !value.includes("@")) value = `https://${value}`;
  value = value.replace(/\/$/, "");
  const match = ORIGIN.exec(value);
  const host = match?.groups?.host;
  const port = match?.groups?.port;
  if (!host || (port !== undefined && (Number(port) < 1 || Number(port) > MAX_PORT))) return { ok: false, problem: problemOf(value) };
  return { ok: true, value: port === undefined || port === DEFAULT_PORT ? `https://${host}` : `https://${host}:${port}` };
}

/** Same list, same order. */
export function sameOrigins(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}
